import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Building2, TrendingUp, TrendingDown, Download, FileText, FileSpreadsheet, ChevronRight, X, Briefcase, Calendar, CheckCircle, UserPlus, DollarSign, LayoutGrid, List } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import DateRangeFilter from '../components/DateRangeFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientAnalytics() {
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [user, setUser] = useState(null);
  const [drilldownClient, setDrilldownClient] = useState(null);
  const [positionBreakdown, setPositionBreakdown] = useState([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'cards' | 'table'

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  useEffect(() => {
    if (timeFilter !== 'custom') fetchAnalytics();
  }, [timeFilter]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let url = `${API}/clients/analytics?time_filter=${timeFilter}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setAnalytics(response.data);
    } catch { toast.error('Failed to fetch client analytics'); }
    finally { setLoading(false); }
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/export/client-analytics?format=${format}&time_filter=${timeFilter}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `client_analytics.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch { toast.error('Failed to export report'); }
  };

  const canExport = user && (user.role === 'admin' || user.role === 'team_leader');

  const handleDrilldown = async (client) => {
    setDrilldownClient(client);
    setDrilldownLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients/${client.client_id}/positions-breakdown`, { headers: { Authorization: `Bearer ${token}` } });
      setPositionBreakdown(response.data);
    } catch { toast.error('Failed to fetch position breakdown'); }
    finally { setDrilldownLoading(false); }
  };

  // Compute totals
  const totals = analytics.reduce((acc, c) => ({
    total_positions: acc.total_positions + (c.total_positions || 0),
    open_positions: acc.open_positions + (c.open_positions || 0),
    total_resumes: acc.total_resumes + (c.total_resumes || 0),
    total_interviews: acc.total_interviews + (c.total_interviews || 0),
    total_closures: acc.total_closures + (c.total_closures || 0),
    total_joinings: acc.total_joinings + (c.total_joinings || 0),
    total_revenue: acc.total_revenue + (c.total_revenue || 0),
  }), { total_positions: 0, open_positions: 0, total_resumes: 0, total_interviews: 0, total_closures: 0, total_joinings: 0, total_revenue: 0 });

  const totalResumeToClosure = totals.total_resumes > 0 ? ((totals.total_closures / totals.total_resumes) * 100).toFixed(1) : '0';
  const totalClosureToJoining = totals.total_closures > 0 ? ((totals.total_joinings / totals.total_closures) * 100).toFixed(0) : '0';
  const totalJoiningToPosition = totals.total_positions > 0 ? ((totals.total_joinings / totals.total_positions) * 100).toFixed(0) : '0';

  const getJoiningToPositionRate = (c) => c.total_positions > 0 ? ((c.total_joinings / c.total_positions) * 100).toFixed(0) : '0';

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;

  return (
    <div data-testid="client-analytics-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-blue-900 mb-1" data-testid="client-analytics-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Client Analytics</h1>
          <p className="text-slate-500 text-sm">Comprehensive client-wise performance metrics</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* View Toggle */}
          <div className="flex bg-blue-50 rounded-lg p-0.5 border border-blue-200">
            <button data-testid="view-cards" onClick={() => setViewMode('cards')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'text-blue-600 hover:bg-blue-100'}`}>
              <LayoutGrid size={13} />Cards
            </button>
            <button data-testid="view-table" onClick={() => setViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-blue-600 hover:bg-blue-100'}`}>
              <List size={13} />Table
            </button>
          </div>
          <DateRangeFilter timeFilter={timeFilter} setTimeFilter={setTimeFilter} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} onApplyRange={fetchAnalytics} />
          {canExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="export-analytics-button" variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                  <Download size={16} className="mr-2" />Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('excel')}><FileSpreadsheet size={16} className="mr-2" />Excel</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('pdf')}><FileText size={16} className="mr-2" />PDF</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {analytics.length === 0 ? (
        <div className="text-center py-16 bg-white border border-blue-100 rounded-xl">
          <Building2 size={48} className="mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">No client data available for this period</p>
        </div>
      ) : viewMode === 'cards' ? (
        /* ======= CARDS VIEW ======= */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="cards-view">
          {analytics.map(client => {
            const r2c = client.total_resumes > 0 ? ((client.total_closures / client.total_resumes) * 100).toFixed(1) : '0';
            const c2j = client.total_closures > 0 ? ((client.total_joinings / client.total_closures) * 100).toFixed(1) : '0';
            const j2p = client.total_positions > 0 ? ((client.total_joinings / client.total_positions) * 100).toFixed(1) : '0';
            return (
            <div key={client.client_id} data-testid={`client-card-${client.client_id}`}
              className="bg-white border border-blue-100 rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
              onClick={() => handleDrilldown(client)}>
              {/* Header */}
              <div className="flex items-center justify-between px-5 pt-5 pb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-extrabold text-lg shrink-0">
                    {client.client_name?.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-extrabold text-slate-900 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>{client.client_name}</h3>
                    <p className="text-xs text-slate-400">{client.open_positions} open / {client.total_positions} total positions</p>
                  </div>
                </div>
                <ChevronRight size={20} className="text-blue-400 shrink-0" />
              </div>

              {/* Row 1: Core Metrics */}
              <div className="grid grid-cols-3 border-t border-slate-100">
                <div className="text-center py-4 border-r border-slate-100">
                  <Briefcase size={20} className="mx-auto text-slate-500 mb-1.5" />
                  <p className="text-2xl font-extrabold text-slate-900">{client.total_positions}</p>
                  <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">Positions</p>
                </div>
                <div className="text-center py-4 border-r border-slate-100">
                  <FileText size={20} className="mx-auto text-blue-500 mb-1.5" />
                  <p className="text-2xl font-extrabold text-slate-900">{client.total_resumes}</p>
                  <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">Resumes</p>
                </div>
                <div className="text-center py-4">
                  <Calendar size={20} className="mx-auto text-amber-500 mb-1.5" />
                  <p className="text-2xl font-extrabold text-slate-900">{client.total_interviews}</p>
                  <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">Interviews</p>
                </div>
              </div>

              {/* Row 2: Closures, Joinings, Revenue */}
              <div className="grid grid-cols-3 border-t border-slate-100">
                <div className="text-center py-4 border-r border-slate-100">
                  <CheckCircle size={20} className="mx-auto text-teal-500 mb-1.5" />
                  <p className="text-2xl font-extrabold text-slate-900">{client.total_closures}</p>
                  <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">Closures</p>
                </div>
                <div className="text-center py-4 border-r border-slate-100">
                  <UserPlus size={20} className="mx-auto text-blue-600 mb-1.5" />
                  <p className="text-2xl font-extrabold text-slate-900">{client.total_joinings}</p>
                  <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">Joinings</p>
                </div>
                <div className="text-center py-4">
                  <DollarSign size={20} className="mx-auto text-green-600 mb-1.5" />
                  <p className="text-2xl font-extrabold text-slate-900">{client.total_revenue ? `\u20B9${client.total_revenue.toLocaleString('en-IN')}` : '\u20B90'}</p>
                  <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">Revenue</p>
                </div>
              </div>

              {/* Row 3: Percentage Ratios */}
              <div className="grid grid-cols-3 border-t border-slate-100 bg-slate-50/50">
                <div className="text-center py-3.5 border-r border-slate-100">
                  <p className={`text-xl font-extrabold ${parseFloat(r2c) > 10 ? 'text-emerald-600' : 'text-amber-600'}`}>{r2c}%</p>
                  <p className="text-[8px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">Resume to Closure</p>
                </div>
                <div className="text-center py-3.5 border-r border-slate-100">
                  <p className={`text-xl font-extrabold ${parseFloat(c2j) > 50 ? 'text-emerald-600' : 'text-amber-600'}`}>{c2j}%</p>
                  <p className="text-[8px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">Closure to Joining</p>
                </div>
                <div className="text-center py-3.5">
                  <p className={`text-xl font-extrabold ${parseFloat(j2p) > 0 ? 'text-emerald-600' : 'text-amber-600'}`}>{j2p}%</p>
                  <p className="text-[8px] font-bold uppercase tracking-wider text-slate-400 mt-0.5">Joining to Position</p>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        /* ======= TABLE VIEW ======= */
        <div className="bg-white border border-blue-100 rounded-xl shadow-sm overflow-hidden" data-testid="table-view">
          <Table>
            <TableHeader>
              <TableRow className="bg-blue-50 border-b border-blue-100">
                <TableHead className="font-bold text-blue-900 text-xs">Client Name</TableHead>
                <TableHead className="font-bold text-blue-900 text-xs text-center">Total Positions</TableHead>
                <TableHead className="font-bold text-blue-900 text-xs text-center">Open Positions</TableHead>
                <TableHead className="font-bold text-blue-900 text-xs text-center">Resumes</TableHead>
                <TableHead className="font-bold text-blue-900 text-xs text-center">Interviews</TableHead>
                <TableHead className="font-bold text-blue-900 text-xs text-center">Closures</TableHead>
                <TableHead className="font-bold text-blue-900 text-xs text-center">Joinings</TableHead>
                <TableHead className="font-bold text-blue-900 text-xs text-center">Resume to Closure</TableHead>
                <TableHead className="font-bold text-blue-900 text-xs text-center">Closure to Joining</TableHead>
                <TableHead className="font-bold text-blue-900 text-xs text-center">Joining to Position</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analytics.map(client => {
                const j2p = getJoiningToPositionRate(client);
                return (
                  <TableRow key={client.client_id} data-testid={`client-row-${client.client_id}`} className="hover:bg-blue-50/50 cursor-pointer" onClick={() => handleDrilldown(client)}>
                    <TableCell className="font-semibold text-blue-900">
                      <span className="flex items-center gap-1">{client.client_name}<ChevronRight size={14} className="text-blue-400" /></span>
                    </TableCell>
                    <TableCell className="text-center font-bold text-slate-700">{client.total_positions}</TableCell>
                    <TableCell className="text-center">
                      <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-bold text-sm">{client.open_positions}</span>
                    </TableCell>
                    <TableCell className="text-center font-bold text-blue-700">{client.total_resumes}</TableCell>
                    <TableCell className="text-center font-bold text-cyan-700">{client.total_interviews}</TableCell>
                    <TableCell className="text-center font-bold text-teal-700">{client.total_closures}</TableCell>
                    <TableCell className="text-center font-bold text-emerald-700">{client.total_joinings}</TableCell>
                    <TableCell className="text-center">
                      <span className="flex items-center justify-center gap-1">
                        {client.resume_to_closure_rate > 10 ? <TrendingUp size={13} className="text-emerald-600" /> : <TrendingDown size={13} className="text-amber-600" />}
                        <span className={`font-bold text-sm ${client.resume_to_closure_rate > 10 ? 'text-emerald-700' : 'text-amber-700'}`}>{client.resume_to_closure_rate}%</span>
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="flex items-center justify-center gap-1">
                        {client.closure_to_joining_rate > 50 ? <TrendingUp size={13} className="text-emerald-600" /> : <TrendingDown size={13} className="text-amber-600" />}
                        <span className={`font-bold text-sm ${client.closure_to_joining_rate > 50 ? 'text-emerald-700' : 'text-amber-700'}`}>{client.closure_to_joining_rate}%</span>
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="flex items-center justify-center gap-1">
                        {parseInt(j2p) > 0 ? <TrendingUp size={13} className="text-emerald-600" /> : <TrendingDown size={13} className="text-amber-600" />}
                        <span className={`font-bold text-sm ${parseInt(j2p) > 0 ? 'text-emerald-700' : 'text-amber-700'}`}>{j2p}%</span>
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
              {/* Summary TOTAL Row */}
              <TableRow className="bg-blue-50/80 border-t-2 border-blue-300" data-testid="summary-row">
                <TableCell className="font-extrabold text-blue-900 text-sm">TOTAL ({analytics.length} CLIENTS)</TableCell>
                <TableCell className="text-center font-extrabold text-slate-900">{totals.total_positions}</TableCell>
                <TableCell className="text-center font-extrabold text-blue-800">{totals.open_positions}</TableCell>
                <TableCell className="text-center font-extrabold text-blue-700">{totals.total_resumes}</TableCell>
                <TableCell className="text-center font-extrabold text-cyan-700">{totals.total_interviews}</TableCell>
                <TableCell className="text-center font-extrabold text-teal-700">{totals.total_closures}</TableCell>
                <TableCell className="text-center font-extrabold text-emerald-700">{totals.total_joinings}</TableCell>
                <TableCell className="text-center font-extrabold text-emerald-700">{totalResumeToClosure}%</TableCell>
                <TableCell className="text-center font-extrabold text-emerald-700">{totalClosureToJoining}%</TableCell>
                <TableCell className="text-center font-extrabold text-emerald-700">{totalJoiningToPosition}%</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}

      {/* Drilldown Dialog */}
      <Dialog open={!!drilldownClient} onOpenChange={(open) => { if (!open) setDrilldownClient(null); }}>
        <DialogContent className="max-w-4xl rounded">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {drilldownClient?.client_name} — Position Breakdown
            </DialogTitle>
          </DialogHeader>
          {drilldownLoading ? (
            <div className="text-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto" /></div>
          ) : (
            <div className="overflow-auto max-h-[60vh]">
              <Table>
                <TableHeader>
                  <TableRow className="bg-blue-50">
                    <TableHead className="font-bold text-blue-900 text-xs">Position</TableHead>
                    <TableHead className="font-bold text-blue-900 text-xs">Status</TableHead>
                    <TableHead className="font-bold text-blue-900 text-xs">Assigned To</TableHead>
                    <TableHead className="font-bold text-blue-900 text-xs text-center">Resumes</TableHead>
                    <TableHead className="font-bold text-blue-900 text-xs text-center">Interviews</TableHead>
                    <TableHead className="font-bold text-blue-900 text-xs text-center">Closures</TableHead>
                    <TableHead className="font-bold text-blue-900 text-xs text-center">Joinings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {positionBreakdown.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-6">No positions found</TableCell></TableRow>
                  ) : positionBreakdown.map(pos => (
                    <TableRow key={pos.position_id} data-testid={`drilldown-pos-${pos.position_id}`} className="hover:bg-blue-50/50">
                      <TableCell className="font-semibold text-blue-900 text-sm">{pos.title}</TableCell>
                      <TableCell>
                        <Badge className={`text-[10px] font-bold ${pos.status === 'open' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-800'}`}>{pos.status?.toUpperCase()}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">{pos.assigned_to_name || '-'}</TableCell>
                      <TableCell className="text-center font-bold text-blue-700">{pos.resumes}</TableCell>
                      <TableCell className="text-center font-bold text-cyan-700">{pos.interviews}</TableCell>
                      <TableCell className="text-center font-bold text-emerald-700">{pos.closures}</TableCell>
                      <TableCell className="text-center font-bold text-violet-700">{pos.joinings}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
